import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { scoreColor as themeScoreColor, categoryColor as themeCategoryColor } from '../utils/themeColors';
import {
  Brain,
  Target,
  TrendingUp,
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertTriangle,
  Zap,
  Award,
  Briefcase,
  MapPin,
  ExternalLink,
  SlidersHorizontal,
  Sparkles,
  ArrowUpRight,
  Shield,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────

interface RankedSkill {
  skill: string;
  category: string;
  proficiency_level: string;
  years_experience: number;
  evidence_count: number;
  confidence: number;
  market_demand_score: number;
  composite_rank_score: number;
  is_power_skill: boolean;
  is_gap_skill: boolean;
}

interface SkillHeatmapEntry {
  category: string;
  skill_count: number;
  avg_proficiency: number;
  avg_market_demand: number;
}

interface SkillData {
  ranked_skills: RankedSkill[];
  power_skills: RankedSkill[];
  gap_skills: RankedSkill[];
  heatmap: SkillHeatmapEntry[];
  total_skills: number;
}

interface OpportunityRanking {
  job_id: number;
  job_title: string;
  company_name: string;
  location: string;
  remote_policy: string;
  application_url: string;
  experience_level: string;
  salary_range: string;
  company_industry: string;
  suitability_score: number;
  selectability_score: number;
  trajectory_score: number;
  market_demand_score: number;
  compensation_score: number;
  composite_score: number;
  matched_skills: string[];
  missing_skills: string[];
  skill_gaps: Array<{ skill: string; severity: number; recommendation: string }>;
  explanation: string;
  rank_position: number;
}

interface TrajectoryData {
  current_title: string;
  target_titles: string[];
  experience_years: number;
  seniority_level: string;
  stepping_stone_roles: Array<{ title: string; company_name: string; trajectory_score: number; composite_score: number }>;
  skill_investments: RankedSkill[];
  power_skills: RankedSkill[];
}

interface MarketData {
  skill_demands: Array<{ skill: string; job_count: number; demand_ratio: number; category: string }>;
  industry_health: Array<{ industry: string; open_roles: number; companies: number }>;
  trending_up: Array<{ skill: string; job_count: number }>;
  trending_down: Array<{ skill: string; job_count: number }>;
  user_market_position: {
    match_percentage: number;
    strongest_market: string;
    competitive_advantage: string[];
    biggest_gap: string;
  };
}

interface ScoringPrefs {
  suitability_weight: number;
  selectability_weight: number;
  trajectory_weight: number;
  market_demand_weight: number;
  compensation_weight: number;
  preset_name: string;
}

type Tab = 'skills' | 'opportunities' | 'trajectory' | 'market';

// ─── Component ──────────────────────────────────────────

const Intelligence: React.FC = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('skills');
  const [skillData, setSkillData] = useState<SkillData | null>(null);
  const [rankings, setRankings] = useState<OpportunityRanking[]>([]);
  const [trajectoryData, setTrajectoryData] = useState<TrajectoryData | null>(null);
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [prefs, setPrefs] = useState<ScoringPrefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedJob, setExpandedJob] = useState<number | null>(null);
  const [minScore, setMinScore] = useState(0);
  const [sortBy, setSortBy] = useState('composite_score');

  const fetchSkills = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/skills`, { credentials: 'include' });
      if (res.ok) setSkillData(await res.json());
    } catch { /* skip */ }
  }, []);

  const fetchRankings = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/intelligence/rankings?limit=50&min_score=${minScore}&sort_by=${sortBy}`,
        { credentials: 'include' },
      );
      if (res.ok) {
        const data = await res.json();
        setRankings(data.data || []);
      }
    } catch { /* skip */ }
  }, [minScore, sortBy]);

  const fetchTrajectory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/trajectory`, { credentials: 'include' });
      if (res.ok) setTrajectoryData(await res.json());
    } catch { /* skip */ }
  }, []);

  const fetchMarket = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/market`, { credentials: 'include' });
      if (res.ok) setMarketData(await res.json());
    } catch { /* skip */ }
  }, []);

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/preferences`, { credentials: 'include' });
      if (res.ok) setPrefs(await res.json());
    } catch { /* skip */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSkills(), fetchPrefs()]).finally(() => setLoading(false));
  }, [fetchSkills, fetchPrefs]);

  useEffect(() => {
    if (activeTab === 'opportunities') fetchRankings();
    if (activeTab === 'trajectory') fetchTrajectory();
    if (activeTab === 'market') fetchMarket();
  }, [activeTab, fetchRankings, fetchTrajectory, fetchMarket]);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API_BASE}/api/intelligence/rankings/refresh`, {
        method: 'POST', credentials: 'include',
      });
      await Promise.all([fetchSkills(), fetchRankings(), fetchTrajectory(), fetchMarket()]);
    } catch { /* skip */ }
    setRefreshing(false);
  };

  const handleRefreshSkills = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API_BASE}/api/intelligence/skills/refresh`, {
        method: 'POST', credentials: 'include',
      });
      await fetchSkills();
    } catch { /* skip */ }
    setRefreshing(false);
  };

  const tabs: Array<{ id: Tab; label: string; icon: React.ElementType }> = [
    { id: 'skills', label: 'Skill Ranker', icon: Brain },
    { id: 'opportunities', label: 'Opportunity Ranker', icon: Target },
    { id: 'trajectory', label: 'Career Trajectory', icon: TrendingUp },
    { id: 'market', label: 'Market Insights', icon: BarChart3 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-purple-600" />
            Intelligence Engine
          </h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1 text-sm">
            AI-powered skill ranking, opportunity scoring, and career insights
          </p>
          <div className="flex items-center gap-3 mt-2">
            <Link
              to="/linkedin-optimizer"
              className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Optimize LinkedIn profile
            </Link>
            <span className="text-gray-300 dark:text-slate-600">·</span>
            <Link
              to="/skill-gap-analysis"
              className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Deep skill gap analysis
            </Link>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleRefreshAll}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Scoring...' : 'Re-Score All'}
        </motion.button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-slate-800">
        <nav className="flex space-x-8">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="space-y-4 animate-pulse">
          <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 p-6">
            <div className="h-5 bg-gray-100 dark:bg-slate-800/50 rounded w-40 mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-6 h-4 bg-gray-100 dark:bg-slate-800/50 rounded" />
                  <div className="flex-1 h-4 bg-gray-100 dark:bg-slate-800/50 rounded" />
                  <div className="w-12 h-4 bg-gray-100 dark:bg-slate-800/50 rounded" />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 h-48" />
            <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 h-48" />
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'skills' && <SkillRankerTab data={skillData} onRefresh={handleRefreshSkills} refreshing={refreshing} />}
          {activeTab === 'opportunities' && (
            <OpportunityRankerTab
              rankings={rankings}
              expandedJob={expandedJob}
              setExpandedJob={setExpandedJob}
              minScore={minScore}
              setMinScore={setMinScore}
              sortBy={sortBy}
              setSortBy={setSortBy}
              prefs={prefs}
            />
          )}
          {activeTab === 'trajectory' && <TrajectoryTab data={trajectoryData} />}
          {activeTab === 'market' && <MarketTab data={marketData} />}
        </>
      )}
    </div>
  );
};

// ─── Skill Ranker Tab ───────────────────────────────────

function SkillRankerTab({ data, onRefresh, refreshing }: { data: SkillData | null; onRefresh: () => void; refreshing: boolean }) {
  const { isDark } = useTheme();
  if (!data || data.total_skills === 0) {
    return (
      <div className="text-center py-16">
        <Brain className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No skills mapped yet</h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 max-w-md mx-auto mb-4">
          Upload a resume or complete an AI interview to build your skill map.
          For the most accurate analysis, also{' '}
          <Link to="/linkedin-optimizer" className="text-purple-600 hover:text-purple-800 underline">
            import your LinkedIn data export
          </Link>.
        </p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
        >
          Refresh Skills
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Power Skills */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Power Skills (Top 10)
          </h2>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onRefresh}
            disabled={refreshing}
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </motion.button>
        </div>
        <div className="space-y-3">
          {data.power_skills.map((skill, i) => (
            <div key={skill.skill} className="flex items-center gap-4">
              <span className="text-xs font-mono text-gray-400 dark:text-slate-500 w-6">#{i + 1}</span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{skill.skill}</span>
                  <div className="flex items-center gap-2">
                    <ProficiencyBadge level={skill.proficiency_level} />
                    <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">{skill.composite_rank_score}/100</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 dark:bg-slate-800/50 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${skill.composite_rank_score}%`,
                      backgroundColor: scoreColor(skill.composite_rank_score, isDark),
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Skill Heatmap */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-500" />
            Skill Distribution
          </h2>
          <div className="space-y-3">
            {data.heatmap.map((entry) => (
              <div key={entry.category} className="flex items-center gap-3">
                <span className="text-sm text-gray-600 dark:text-slate-400 w-24 capitalize">{entry.category}</span>
                <div className="flex-1 bg-gray-100 dark:bg-slate-800/50 rounded-full h-6 relative">
                  <div
                    className="h-6 rounded-full flex items-center justify-end pr-2 text-xs font-medium text-white transition-all"
                    style={{
                      width: `${Math.max((entry.skill_count / Math.max(data.total_skills, 1)) * 100, 10)}%`,
                      backgroundColor: categoryColor(entry.category, isDark),
                    }}
                  >
                    {entry.skill_count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gap Skills */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Skill Gaps (Invest Here)
          </h2>
          {data.gap_skills.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400">No significant gaps detected. Great coverage!</p>
          ) : (
            <div className="space-y-3">
              {data.gap_skills.map((skill) => (
                <div key={skill.skill} className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800/30">
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{skill.skill}</span>
                    <p className="text-xs text-gray-500 dark:text-slate-400">In {Math.round(skill.market_demand_score * 100)}% of target jobs</p>
                  </div>
                  <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded">Gap</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* All Skills Table */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">All Skills ({data.total_skills})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-800">
                <th className="text-left py-2 text-gray-500 dark:text-slate-400 font-medium">Skill</th>
                <th className="text-left py-2 text-gray-500 dark:text-slate-400 font-medium">Category</th>
                <th className="text-left py-2 text-gray-500 dark:text-slate-400 font-medium">Proficiency</th>
                <th className="text-right py-2 text-gray-500 dark:text-slate-400 font-medium">Evidence</th>
                <th className="text-right py-2 text-gray-500 dark:text-slate-400 font-medium">Market Demand</th>
                <th className="text-right py-2 text-gray-500 dark:text-slate-400 font-medium">Score</th>
              </tr>
            </thead>
            <tbody>
              {data.ranked_skills.slice(0, 30).map((skill) => (
                <tr key={skill.skill} className="border-b border-gray-50 dark:border-slate-800/50 hover:bg-gray-50 dark:hover:bg-slate-800">
                  <td className="py-2 font-medium text-gray-900 dark:text-white">{skill.skill}</td>
                  <td className="py-2">
                    <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${categoryColor(skill.category, isDark)}20`, color: categoryColor(skill.category, isDark) }}>
                      {skill.category}
                    </span>
                  </td>
                  <td className="py-2"><ProficiencyBadge level={skill.proficiency_level} /></td>
                  <td className="py-2 text-right text-gray-600 dark:text-slate-400">{skill.evidence_count} sources</td>
                  <td className="py-2 text-right">
                    <span className={`text-xs font-medium ${skill.market_demand_score > 0.5 ? 'text-green-600' : 'text-gray-500 dark:text-slate-400'}`}>
                      {Math.round(skill.market_demand_score * 100)}%
                    </span>
                  </td>
                  <td className="py-2 text-right font-semibold" style={{ color: scoreColor(skill.composite_rank_score, isDark) }}>
                    {skill.composite_rank_score}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Opportunity Ranker Tab ─────────────────────────────

function OpportunityRankerTab({
  rankings, expandedJob, setExpandedJob, minScore, setMinScore, sortBy, setSortBy, prefs,
}: {
  rankings: OpportunityRanking[];
  expandedJob: number | null;
  setExpandedJob: (id: number | null) => void;
  minScore: number;
  setMinScore: (v: number) => void;
  sortBy: string;
  setSortBy: (v: string) => void;
  prefs: ScoringPrefs | null;
}) {
  const { isDark } = useTheme();
  if (rankings.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="No opportunities ranked yet"
        description="Click 'Re-Score All' in the page header to analyse open jobs against your profile. Make sure you have scraped jobs first from the Jobs page."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-gray-500 dark:text-slate-400" />
          <label className="text-sm text-gray-600 dark:text-slate-400">Min Score:</label>
          <input
            type="range"
            min={0}
            max={90}
            step={10}
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value, 10))}
            className="w-24"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{minScore}</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-slate-400">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-gray-300 dark:border-slate-700 rounded-md px-2 py-1 dark:bg-slate-800 dark:text-white"
          >
            <option value="composite_score">Composite</option>
            <option value="suitability_score">Suitability</option>
            <option value="selectability_score">Selectability</option>
            <option value="trajectory_score">Trajectory</option>
            <option value="market_demand_score">Market Demand</option>
            <option value="compensation_score">Compensation</option>
          </select>
        </div>
        {prefs && (
          <div className="text-xs text-gray-400 dark:text-slate-500 ml-auto">
            Weights: Suit {Math.round(prefs.suitability_weight * 100)}% | Sel {Math.round(prefs.selectability_weight * 100)}% | Traj {Math.round(prefs.trajectory_weight * 100)}% | Mkt {Math.round(prefs.market_demand_weight * 100)}% | Comp {Math.round(prefs.compensation_weight * 100)}%
          </div>
        )}
      </div>

      {/* Rankings */}
      {rankings.map((r) => (
        <motion.div
          key={r.job_id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden"
        >
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            onClick={() => setExpandedJob(expandedJob === r.job_id ? null : r.job_id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-purple-600">#{r.rank_position}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{r.job_title}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                      <Briefcase className="w-3 h-3" />
                      <span>{r.company_name}</span>
                      {r.location && (
                        <>
                          <MapPin className="w-3 h-3 ml-2" />
                          <span>{r.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold" style={{ color: scoreColor(r.composite_score, isDark) }}>
                    {r.composite_score}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">composite</div>
                </div>
                {expandedJob === r.job_id ? <ChevronUp className="w-5 h-5 text-gray-400 dark:text-slate-500" /> : <ChevronDown className="w-5 h-5 text-gray-400 dark:text-slate-500" />}
              </div>
            </div>

            {/* Score bars (always visible) */}
            <div className="grid grid-cols-5 gap-2 mt-3">
              <ScoreBar label="Suitability" score={r.suitability_score} />
              <ScoreBar label="Selectability" score={r.selectability_score} />
              <ScoreBar label="Trajectory" score={r.trajectory_score} />
              <ScoreBar label="Market" score={r.market_demand_score} />
              <ScoreBar label="Comp" score={r.compensation_score} />
            </div>
          </div>

          {/* Expanded details */}
          {expandedJob === r.job_id && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="border-t border-gray-100 dark:border-slate-800 p-4 bg-gray-50 dark:bg-slate-800/50"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Matched Skills */}
                <div>
                  <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Matched Skills ({r.matched_skills.length})
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {r.matched_skills.map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>

                {/* Skill Gaps */}
                <div>
                  <h4 className="text-sm font-medium text-orange-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Gaps ({r.skill_gaps.length})
                  </h4>
                  <div className="space-y-1">
                    {r.skill_gaps.slice(0, 5).map((g) => (
                      <div key={g.skill} className="text-xs flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${g.severity >= 0.7 ? 'bg-red-400' : 'bg-yellow-400'}`} />
                        <span className="text-gray-700 dark:text-slate-300">{g.skill}</span>
                        <span className="text-gray-400 dark:text-slate-500 italic">{g.recommendation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Explanation */}
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-3 bg-white dark:bg-navy-800 p-3 rounded-xl border border-gray-200 dark:border-slate-800">
                {r.explanation}
              </p>

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                {r.application_url && (
                  <a
                    href={r.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                  >
                    <ExternalLink className="w-3 h-3" /> Apply
                  </a>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
  );
}

// ─── Career Trajectory Tab ──────────────────────────────

function TrajectoryTab({ data }: { data: TrajectoryData | null }) {
  const { isDark } = useTheme();
  if (!data) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="No trajectory data"
        description="Complete your profile and score jobs to see career trajectory insights."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Position */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <ArrowUpRight className="w-5 h-5 text-green-500" />
          Career Path
        </h2>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-2">
            <div className="text-xs text-primary-500 uppercase font-medium">Current</div>
            <div className="text-sm font-semibold text-primary-900">{data.current_title || 'Not set'}</div>
          </div>
          <div className="text-gray-400 dark:text-slate-500">→</div>
          {data.target_titles.map((t) => (
            <div key={t} className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-2">
              <div className="text-xs text-purple-500 uppercase font-medium">Target</div>
              <div className="text-sm font-semibold text-purple-900">{t}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-sm text-gray-500 dark:text-slate-400">
          {data.experience_years} years experience | Level: {data.seniority_level}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stepping Stone Roles */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Top Stepping Stone Roles
          </h2>
          {data.stepping_stone_roles.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400">Score jobs first to see trajectory-ranked roles.</p>
          ) : (
            <div className="space-y-3">
              {data.stepping_stone_roles.map((role, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                  <span className="text-lg font-bold text-amber-500">#{i + 1}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{role.title}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">{role.company_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold" style={{ color: scoreColor(role.trajectory_score, isDark) }}>
                      {role.trajectory_score}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-slate-500">trajectory</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Skill Investment Priorities */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-500" />
            Skill Investment Priorities
          </h2>
          <div className="space-y-3">
            {data.skill_investments.map((skill) => (
              <div key={skill.skill} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-100 dark:border-red-800/30">
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{skill.skill}</span>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    Market demand: {Math.round(skill.market_demand_score * 100)}%
                  </p>
                </div>
                <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">Invest</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Market Insights Tab ────────────────────────────────

function MarketTab({ data }: { data: MarketData | null }) {
  if (!data) {
    return (
      <EmptyState
        icon={BarChart3}
        title="No market data"
        description="Score jobs to generate market demand insights."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* User Market Position */}
      <div className="bg-gradient-to-r from-purple-50 to-primary-50 dark:from-purple-900/20 dark:to-primary-900/20 rounded-xl border border-purple-200 dark:border-purple-800/30 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-600" />
          Your Market Position
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-navy-800 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">{data.user_market_position.match_percentage}%</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">Job Coverage</div>
          </div>
          <div className="bg-white dark:bg-navy-800 rounded-xl p-3 text-center">
            <div className="text-sm font-bold text-gray-900 dark:text-white">{data.user_market_position.strongest_market}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">Strongest Market</div>
          </div>
          <div className="bg-white dark:bg-navy-800 rounded-xl p-3">
            <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Competitive Edge</div>
            <div className="flex flex-wrap gap-1">
              {data.user_market_position.competitive_advantage.map((s) => (
                <span key={s} className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">{s}</span>
              ))}
            </div>
          </div>
          <div className="bg-white dark:bg-navy-800 rounded-xl p-3 text-center">
            <div className="text-sm font-bold text-orange-600 capitalize">{data.user_market_position.biggest_gap}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">Biggest Gap</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Industry Health */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Industry Health</h2>
          <div className="space-y-3">
            {data.industry_health.slice(0, 8).map((ind) => (
              <div key={ind.industry} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-slate-300">{ind.industry}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{ind.open_roles} roles</span>
                  <span className="text-xs text-gray-400 dark:text-slate-500">{ind.companies} companies</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trending Skills */}
        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Trending Skills</h2>
          <div className="space-y-2">
            {data.trending_up.slice(0, 10).map((skill, i) => (
              <div key={skill.skill} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 dark:text-slate-500 w-5">#{i + 1}</span>
                <span className="text-sm text-gray-700 dark:text-slate-300 flex-1">{skill.skill}</span>
                <span className="text-sm font-medium text-green-600">{skill.job_count} jobs</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────

function ScoreBar({ label, score }: { label: string; score: number }) {
  const { isDark } = useTheme();
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-gray-500 dark:text-slate-400">{label}</span>
        <span className="font-medium" style={{ color: scoreColor(score, isDark) }}>{score}</span>
      </div>
      <div className="w-full bg-gray-100 dark:bg-slate-800/50 rounded-full h-1.5">
        <div
          className="h-1.5 rounded-full transition-all"
          style={{ width: `${score}%`, backgroundColor: scoreColor(score, isDark) }}
        />
      </div>
    </div>
  );
}

function ProficiencyBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    expert: 'bg-purple-100 text-purple-700',
    advanced: 'bg-primary-100 text-primary-700',
    intermediate: 'bg-green-100 text-green-700',
    beginner: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${colors[level] || colors.intermediate}`}>
      {level}
    </span>
  );
}

function EmptyState({ icon: Icon, title, description, actionLabel, onAction }: {
  icon: React.ElementType;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="text-center py-16">
      <Icon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-slate-400 max-w-md mx-auto">{description}</p>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="mt-4 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────

function scoreColor(score: number, isDark: boolean = false): string {
  return themeScoreColor(score, isDark);
}

function categoryColor(category: string, isDark: boolean = false): string {
  return themeCategoryColor(category, isDark);
}

export default Intelligence;
